import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AIRepellantHero from '../components/heroes/AIRepellantHero';

// Worker base URL — set via Cloudflare Pages env var (REACT_APP_WORKER_URL)
const WORKER_URL = process.env.REACT_APP_WORKER_URL;

// Maps drop slugs to their custom hero components.
// Add a new entry here whenever a new drop is created.
const HERO_MAP = {
  'ai-repellant': AIRepellantHero,
};

// Full list of US state abbreviations for the shipping address state dropdown.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

export default function RecipientPage() {
  // Pull slug and token from the URL: /r/:slug/:token
  // Example: /r/ai-repellant/87124070
  var params = useParams();
  var slug = params.slug;
  var token = params.token;

  // Drop data from Airtable Drops table (buyer-specific: unit count, units claimed, status, buyer name)
  var [drop, setDrop] = useState(null);
  // Catalog data from Airtable Catalog table (drop name, description, qualifying question, etc.)
  var [catalog, setCatalog] = useState(null);

  // Page load state
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);

  // Claim form submission state
  var [submitted, setSubmitted] = useState(false);   // true after successful claim
  var [submitting, setSubmitting] = useState(false); // true while POST is in flight
  var [submitError, setSubmitError] = useState(null); // error message to show inline

  // Controlled form state — all shipping address fields + optional qualifying answer
  var [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    qualifyingAnswer: '', // Only shown + required if catalog.qualifyingQuestion is set (Lead Qual drops)
  });

  // On mount: fetch drop + catalog data from Worker GET /r/:slug/:token
  // Worker validates the slug+token combo against the Drops table,
  // then fetches the matching Catalog record for full drop details.
  useEffect(function() {
    fetch(WORKER_URL + '/r/' + slug + '/' + token)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.error) {
          // Worker returned an error — invalid token, expired link, or Airtable issue
          setError(data.error);
        } else {
          setDrop(data.drop);
          setCatalog(data.catalog);
        }
        setLoading(false);
      })
      .catch(function() {
        // Network failure or Worker unreachable
        setError('Failed to load drop.');
        setLoading(false);
      });
  }, [slug, token]);

  // Generic change handler for all form inputs and selects.
  // Uses input `name` attribute to update the matching key in form state.
  function handleChange(e) {
    var val = e.target.value;
    setForm(function(prev) {
      var next = {};
      Object.keys(prev).forEach(function(k) { next[k] = prev[k]; });
      next[e.target.name] = val;
      return next;
    });
  }

  // Form submission handler — POSTs claim data to Worker POST /claim
  // Worker re-validates the token, checks units remaining, then writes
  // a new record to the Airtable Claims table.
  function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    // Build the POST payload. dropSlug + token are used by the Worker
    // to re-validate the link before writing the claim.
    var payload = {
      dropSlug: slug,
      token: token,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      company: form.company,
      address1: form.address1,
      address2: form.address2,
      city: form.city,
      state: form.state,
      zip: form.zip,
    };

    // Only include qualifyingAnswer if this is a Lead Qual drop
    // (i.e., the catalog record has a qualifyingQuestion value)
    if (catalog && catalog.qualifyingQuestion) {
      payload.qualifyingAnswer = form.qualifyingAnswer;
    }

    fetch(WORKER_URL + '/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.error === 'sold_out') {
          // Race condition — drop sold out between page load and form submit.
          // Update local state to flip the page into sold-out view immediately.
          setDrop(function(prev) {
            var next = {};
            Object.keys(prev).forEach(function(k) { next[k] = prev[k]; });
            next.unitsRemaining = 0;
            return next;
          });
          setSubmitError('Sorry — the last one was just claimed. You just missed it.');
        } else if (data.error) {
          // Any other Worker error (invalid link, Airtable write failure, etc.)
          setSubmitError(data.error);
        } else {
          // Claim written successfully — flip to success state
          setSubmitted(true);
        }
        setSubmitting(false);
      })
      .catch(function() {
        // Network failure or Worker unreachable during submit
        setSubmitError('Something went wrong. Please try again.');
        setSubmitting(false);
      });
  }

  // ── RENDER STATES ──────────────────────────────────────────

  // Loading: Worker fetch in progress
  if (loading) return <div className="rp-loading">Loading...</div>;

  // Error: invalid/expired link, or Worker/Airtable failure on page load
  if (error) return <div className="rp-error">This link is invalid or has expired.</div>;

  // Resolve hero component for this drop slug (null if no hero registered)
  var HeroComponent = HERO_MAP[slug] || null;

  // Sold out if: no units remaining, OR Drops table status is 'Sold Out',
  // OR Catalog status is 'Sold Out'
  // NOTE: Drops table also uses status 'collecting' for active drops — see Open Tasks
var soldOut = drop.unitsRemaining <= 0 || drop.status === 'fulfilling' || drop.status === 'shipped' || catalog.status === 'Sold Out';

  // Percentage of units claimed — used to fill the scarcity progress bar
  var pct = Math.round(((drop.unitCountPurchased - drop.unitsRemaining) / drop.unitCountPurchased) * 100);

  // Success state: claim was submitted successfully
  if (submitted) {
    return (
      <div className="rp-wrap">
        {HeroComponent && <HeroComponent drop={drop} catalog={catalog} minimal={true} />}
        <div className="rp-success">
          <h2>You're in.</h2>
          <p>Your {catalog.name} is on its way. Keep an eye on your inbox.</p>
        </div>
      </div>
    );
  }

  // Main page: hero + sender signal + scarcity bar + claim form (or sold-out state)
  return (
    <div className="rp-wrap">
      {HeroComponent && <HeroComponent drop={drop} catalog={catalog} />}
      <div className="rp-body">

        {/* Sender attribution — shows the buyer's name who sent this drop */}
        <div className="rp-sender">
          <span>{drop.buyerName} sent you this</span>
        </div>

        {/* Scarcity bar — visual progress indicator of units claimed vs. total */}
        <div className="rp-scarcity">
          <div className="rp-scarcity-bar">
            <div className="rp-scarcity-fill" style={{ width: pct + '%' }}></div>
          </div>
          {soldOut
            ? <span className="rp-scarcity-label rp-sold-out-label">Sold out</span>
            : <span className="rp-scarcity-label"><strong>{drop.unitsRemaining}</strong> of {drop.unitCountPurchased} remaining</span>
          }
        </div>

        {soldOut ? (
          // Sold-out state: drop is fully claimed
          <div className="rp-sold-out">
            <h3>You just missed it.</h3>
            <p>All {drop.unitCountPurchased} have been claimed. Better luck next drop.</p>
          </div>
        ) : (
          // Claim form: collects shipping address (+ qualifying answer for Lead Qual drops)
          <form className="rp-form" onSubmit={handleSubmit}>
            <h3>Claim your {catalog.name}</h3>
            <p className="rp-form-sub">Enter your shipping address and we'll get it out to you.</p>

            <div className="rp-row">
              <div className="rp-field">
                <label>First name</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} required />
              </div>
              <div className="rp-field">
                <label>Last name</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} required />
              </div>
            </div>

            <div className="rp-field">
              <label>Work email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>

            <div className="rp-field">
              <label>Company</label>
              <input name="company" value={form.company} onChange={handleChange} />
            </div>

            <div className="rp-field">
              <label>Address line 1</label>
              <input name="address1" value={form.address1} onChange={handleChange} required />
            </div>

            <div className="rp-field">
              <label>Address line 2 <span className="rp-optional">(optional)</span></label>
              <input name="address2" value={form.address2} onChange={handleChange} />
            </div>

            <div className="rp-row rp-row-city">
              <div className="rp-field rp-field-city">
                <label>City</label>
                <input name="city" value={form.city} onChange={handleChange} required />
              </div>
              <div className="rp-field rp-field-state">
                <label>State</label>
                <select name="state" value={form.state} onChange={handleChange} required>
                  <option value="">—</option>
                  {US_STATES.map(function(s) {
                    return <option key={s} value={s}>{s}</option>;
                  })}
                </select>
              </div>
              <div className="rp-field rp-field-zip">
                <label>Zip</label>
                <input name="zip" value={form.zip} onChange={handleChange} required />
              </div>
            </div>

            {/* Qualifying question — only rendered for Lead Qual drops */}
            {catalog.qualifyingQuestion && (
              <div className="rp-field rp-qual">
                <label>{catalog.qualifyingQuestion}</label>
                <textarea
                  name="qualifyingAnswer"
                  value={form.qualifyingAnswer}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>
            )}

            {/* Inline error message for failed submissions */}
            {submitError && <div className="rp-submit-error">{submitError}</div>}

            <button type="submit" className="rp-submit" disabled={submitting}>
              {submitting ? 'Claiming...' : 'Claim my ' + catalog.name}
            </button>

            <p className="rp-fine">
              We'll ship directly to you. No spam, ever. Only {drop.unitsRemaining} left — first come, first served.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}